import plural from "plural-ru";
import tippy from "tippy.js";
import { canv } from "@/canv/global";
import { saveTextToClipboard } from "@/canvasController/helpers";
import type { CommentMessage } from "@/elements/comment/types";
import { generateColor } from "@/helpers/generateColor";
import { getInitials } from "@/helpers/getInitials";
import { getDate } from "@/utils";
import { CLOSE_ICON, MORE_ICON, RESOLVE_ICON, RESOLVED_ICON } from "./constants";

type IconButtonParams = {
    className: string;
    icon: string;
    uid: string;
    tooltipText: string;
    onClick?: () => void;
    disabled?: boolean;
};

export const createIconButton = (params: IconButtonParams) => {
    const button = document.createElement("button");
    const parser = new DOMParser();
    const svgDoc = parser.parseFromString(params.icon, "image/svg+xml");
    const svgIcon = svgDoc.querySelector("svg");

    button.classList.add("comment-header__button", params.className);
    if (params.disabled) {
        button.classList.add("disabled");
    }
    button.type = "button";
    button["data-tooltip"] = params.tooltipText;
    button["data-uid"] = params.uid;

    if (svgIcon !== null) button.appendChild(svgIcon);
    if (params.onClick) button.onclick = params.onClick;

    return button;
};

export const getAvatar = (accountName: string, color: string) => {
    const avatar = document.createElement("div");

    avatar.classList.add("comment__avatar");
    avatar.style.setProperty("background-color", color);
    avatar.textContent = getInitials(accountName);

    return avatar;
};

export const initNestedTooltips = () => {
    const tooltipContainer = canv.commentsPopovers.window.instance?.popper;
    if (!tooltipContainer) return;

    const innerButtons = tooltipContainer.querySelectorAll(".comment-header__button");

    innerButtons.forEach((button) => {
        if (button.classList.contains("disabled")) return;

        tippy(button, {
            content: button["data-tooltip"],
            placement: "bottom",
            appendTo: () => document.body,
        });
    });
};

type HeaderProps = {
    uid: string;
    isResolved: boolean;
    isResolveDisabled: boolean;
    showDelete: boolean;
    onClose: () => void;
    onResolve: () => void;
    onDelete: () => void;
};

export const getHeader = (params: HeaderProps) => {
    const { uid, isResolved, isResolveDisabled, showDelete, onClose, onResolve, onDelete } = params;

    const header = document.createElement("div");
    header.classList.add("comment-header");

    const moreButton = createIconButton({
        className: "comment-header__actions",
        uid,
        onClick: () => createActionsMenu(header, moreButton, uid, showDelete, onDelete),
        icon: MORE_ICON,
        tooltipText: "Действия",
    });
    const resolveButton = createIconButton({
        className: "comment-header__resolve",
        disabled: isResolveDisabled,
        uid,
        onClick: onResolve,
        icon: isResolved ? RESOLVED_ICON : RESOLVE_ICON,
        tooltipText: isResolved ? "Отметить нерешенным" : "Отметить решенным",
    });
    const closeButton = createIconButton({
        className: "comment-header__close",
        uid,
        onClick: onClose,
        icon: CLOSE_ICON,
        tooltipText: "Закрыть",
    });

    header.appendChild(moreButton);
    header.appendChild(resolveButton);
    header.appendChild(closeButton);

    return header;
};

const createActionsMenu = (
    header: HTMLDivElement,
    moreButton: HTMLButtonElement,
    uid: string,
    showDelete: boolean,
    onDelete: () => void,
) => {
    if (header.querySelector(".comment-actions-menu")) return;

    const menu = document.createElement("div");
    menu.classList.add("comment-actions-menu");

    const linkItem = document.createElement("div");
    linkItem.classList.add("comment-actions-menu__item");
    linkItem.textContent = "Скопировать ссылку";
    linkItem.onclick = (e) => {
        e.stopPropagation();
        saveCommentLinkToClipboard(uid);
        removeClickOutsideListener();
        moreButton.classList.remove("active");
        menu.remove();
    };
    menu.appendChild(linkItem);

    if (showDelete) {
        const deleteItem = document.createElement("div");
        deleteItem.classList.add("comment-actions-menu__item");
        deleteItem.textContent = "Удалить";
        deleteItem.onclick = (e) => {
            e.stopPropagation();
            removeClickOutsideListener();
            moreButton.classList.remove("active");
            menu.remove();
            onDelete();
        };
        menu.appendChild(deleteItem);
    }

    header.appendChild(menu);

    moreButton.classList.add("active");

    const handleClickOutside = (e: MouseEvent) => {
        if (!menu.contains(e.target as Node) && !moreButton.contains(e.target as Node)) {
            removeClickOutsideListener();
            moreButton.classList.remove("active");
            menu.remove();
        }
    };

    const removeClickOutsideListener = () => {
        document.removeEventListener("click", handleClickOutside);
    };

    // Задержка нужна, чтобы не поймать текущий клик на саму кнопку меню
    setTimeout(() => {
        document.addEventListener("click", handleClickOutside);
    }, 0);
};

export const getFooter = (onSubmit: (text: string) => void) => {
    const footer = document.createElement("div");

    footer.classList.add("comment-footer");

    const container = document.createElement("div");
    container.classList.add("comment-footer__container");

    const textarea = document.createElement("textarea");
    textarea.classList.add("comment-footer__textarea", "scrollable");
    textarea.placeholder = "Добавьте ответ";
    textarea.rows = 1;
    textarea.setAttribute("maxlength", "2000");

    const button = document.createElement("button");
    button.classList.add("comment-footer__button");
    button.disabled = true;
    button.innerHTML = `<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path fill-rule="evenodd" clip-rule="evenodd" d="M3.37691 2.10163L17.5179 9.31504C18.1726 9.64904 18.1518 10.5696 17.5082 10.8888L3.3673 17.902C2.75195 18.2072 2 17.7592 2 17.1127V13.686C2 11.8956 3.45146 10.4442 5.24185 10.4444C5.49802 10.4239 5.74059 10.2697 5.74059 9.99936C5.74059 9.75359 5.5173 9.55435 5.24185 9.55435C3.45139 9.55423 2 8.10274 2 6.31228V2.88608C2 2.22114 2.77428 1.79423 3.37691 2.10163Z" fill="#ABABBB"/>
    </svg>`;

    const heightController = (e?: Event) => {
        // @ts-expect-error
        if (e?.target?.value) {
            button.disabled = false;
        } else {
            button.disabled = true;
        }
        textarea.style.height = "auto";
        textarea.style.height = `${textarea.scrollHeight}px`;

        if (textarea.value.length > 2000) {
            textarea.value = textarea.value.substring(0, 2000);
        }

        const popperClientHeight = canv.commentsPopovers.window.instance?.popper.clientHeight || 0;

        if (popperClientHeight > canv.canvas.clientHeight) {
            textarea.style.height = `${textarea.clientHeight - (popperClientHeight - canv.canvas.clientHeight)}px`;
        }
    };

    const focusArea = () => {
        canv.appInterface.inputFocused = 1;
    };

    const blurArea = () => {
        canv.appInterface.inputFocused = 0;
    };

    const submit = () => {
        const value = textarea.value.trim();
        if (value) {
            onSubmit(value);
            textarea.value = "";
            heightController();
        }
    };

    const keydownArea = (e) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            submit();
        }
    };

    textarea.addEventListener("input", heightController);
    textarea.addEventListener("focus", focusArea);
    textarea.addEventListener("blur", blurArea);
    textarea.addEventListener("keydown", keydownArea);

    button.onclick = () => {
        submit();
    };

    container.appendChild(textarea);
    container.appendChild(button);
    footer.appendChild(container);

    return footer;
};

export const getComment = (
    {
        name,
        date,
        text,
        authorUid,
        uid,
    }: { name: string; date: string; text: string; authorUid: string; uid: string },
    showAvatar = false,
) => {
    const dateToPrint = getDate(new Date(date));

    const comment = document.createElement("div");
    const commentContent = document.createElement("div");
    const commentName = document.createElement("div");
    const commentDate = document.createElement("div");
    const commentText = document.createElement("div");

    comment.classList.add("comment");
    comment.setAttribute("data-uid", uid);

    commentName.classList.add("comment__name");
    commentName.textContent = name;

    commentDate.classList.add("comment__date");
    commentDate.textContent = dateToPrint.toLocaleString();

    commentText.classList.add("comment__text");
    commentText.textContent = decodeHTMLEntities(text);

    commentContent.classList.add("comment__content");
    commentContent.appendChild(commentName);
    commentContent.appendChild(commentDate);
    commentContent.appendChild(commentText);

    if (showAvatar) {
        const color = generateColor(authorUid);
        comment.appendChild(getAvatar(name, color));
    } else {
        comment.textContent = "";
    }

    comment.appendChild(commentContent);

    return comment;
};

type CommentParams = {
    accountName: string;
    createdAt: string;
    text: string;
    authorUid: string;
    uid: string;
};

export const getCommentList = (commentParams: CommentParams, messages?: CommentMessage[]) => {
    const commentList = document.createElement("div");
    commentList.classList.add("comment-list", "scrollable");
    const comment = getComment(
        {
            name: commentParams.accountName,
            date: commentParams.createdAt,
            text: commentParams.text,
            authorUid: commentParams.authorUid,
            uid: commentParams.uid,
        },
        true,
    );

    commentList.appendChild(comment);

    if (Array.isArray(messages)) {
        messages.forEach((message) => {
            const commentMessage = getComment(
                {
                    name: message.accountName,
                    date: message.createdAt,
                    text: message.text,
                    authorUid: message.token,
                    uid: message.UID,
                },
                true,
            );

            commentList.appendChild(commentMessage);
        });
    }

    return commentList;
};

export const findInsertIndex = (messages: CommentMessage[], newMsg: CommentMessage) => {
    const newTs = Date.parse(newMsg.createdAt);

    for (let i = 0; i < messages.length; i++) {
        const msgTs = Date.parse(messages[i].createdAt);
        if (newTs < msgTs) {
            return i;
        }
    }

    return messages.length;
};

export const getAnswersCount = (messagesCount: number) => {
    const commentAnswers = document.createElement("div");

    commentAnswers.classList.add("comment-answers");
    commentAnswers.textContent = `${messagesCount ? `${messagesCount} ${plural(messagesCount, "ответ", "ответа", "ответов")}` : ""}`;

    return commentAnswers;
};

export const saveCommentLinkToClipboard = (commentUID: string) => {
    const pathToGroup = `${canv.host}/board/${canv.getInitDataProp("boardUID")}?linkToWidget=${commentUID}`;
    saveTextToClipboard(pathToGroup);
};

const decoderTextArea = document.createElement("textarea");

export const decodeHTMLEntities = (str: string) => {
    decoderTextArea.innerHTML = str;
    return decoderTextArea.value;
};
